// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { CircleAlert, Home, KeyRound, Mail, Pencil, Plus, UserRound, X } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { FormCheckbox, FormCity, FormCountry, FormInput, FormSelect, FormState } from "@/components/forms/FormControls";
import PhoneField from "@/components/forms/PhoneField";
import { DEFAULT_COUNTRY } from "@/lib/phone";
import { clientNamesNeedReview, formatDealPrice, looksLikeAddress } from "@/utils/deals.utils";
import { NEW_DEAL_INPUT_CLASS } from "@/constant/deals.constant";
import { absoluteUrl, cn } from "@/utils/common.utils";
import ImageDrop from "@/components/deals/ImageDrop";
import { newDealPropertySchema, newDealReviewSchema, newDealTransactionSchema } from "@/schemas/newDeal";
import { DealType } from "@/enum/common.enum";

/** A buyer deal only needs an address once there is a property to name — which
 *  is exactly when it goes under contract. */
export function isAddressRequired(form) {
  return form.deal_type !== DealType.BUY || Boolean(form.under_contract);
}

export default function NewDealSteps({ step, form, setForm, fullAddress, onAdvance, onSideChange, onSubmit, onEditStep, showStepCount = true }) {
  return (
    <div className="min-h-0 flex-1 overscroll-contain overflow-y-auto px-4 pb-6 pt-5 sm:px-8 sm:pb-8 sm:pt-7 md:px-10">
      {step === 1 && <TransactionStep form={form} setForm={setForm} onSideChange={onSideChange} onAdvance={onAdvance} showStepCount={showStepCount} />}
      {step === 2 && <PropertyStep form={form} setForm={setForm} onAdvance={onAdvance} showStepCount={showStepCount} />}
      {step === 3 && <ReviewStep form={form} fullAddress={fullAddress} onSubmit={onSubmit} onEditStep={onEditStep} showStepCount={showStepCount} />}
    </div>
  );
}

function TransactionStep({ form, setForm, onSideChange, onAdvance, showStepCount }) {
  const transactionForm = useForm({
    resolver: zodResolver(newDealTransactionSchema),
    defaultValues: {
      deal_type: form.deal_type,
      client_name: form.client_name,
      client_email: form.client_email,
      client_phone: form.client_phone,
      client_phone_country: form.client_phone_country || DEFAULT_COUNTRY,
      co_agent_name: form.co_agent_name,
      co_agent_email: form.co_agent_email,
      co_agent_phone: form.co_agent_phone,
      co_agent_phone_country: form.co_agent_phone_country || DEFAULT_COUNTRY,
      additional_clients: (form.additional_clients || []).map((c) => ({ phone_country: DEFAULT_COUNTRY, ...c })),
      under_contract: Boolean(form.under_contract),
    },
  });
  const additionalClients = useFieldArray({ control: transactionForm.control, name: "additional_clients" });

  // The side lives in the parent so the sidebar, the review card and the
  // address rule all read the same value; the toggle writes through to both.
  const selectSide = (dealType) => {
    if (!dealType) return;
    transactionForm.setValue("deal_type", dealType, { shouldValidate: true });
    onSideChange(dealType);
  };

  const advance = (values) => {
    // Unchecking "under contract" hides the other side's agent; drop whatever
    // was typed there so it is neither invited nor shown on the review step.
    const next = values.under_contract
      ? values
      : { ...values, co_agent_name: "", co_agent_email: "", co_agent_phone: "" };
    setForm((current) => ({ ...current, ...next }));
    onAdvance();
  };

  const dealType = transactionForm.watch("deal_type");
  const underContract = transactionForm.watch("under_contract");

  return (
    <Form {...transactionForm}>
      <form id="new-deal-step-form" onSubmit={transactionForm.handleSubmit(advance)} data-testid="new-deal-step-1" noValidate>
        <StepHeader step={1} title="Let's start with your client" description="Choose your side and add your client's details." showStepCount={showStepCount} />

        <div className="mt-7">
          <SectionHeading>Who do you represent?</SectionHeading>
          <ToggleGroup type="single" value={dealType} onValueChange={selectSide} className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2" aria-label="Who do you represent?">
            <SideOption value={DealType.BUY} selected={dealType === DealType.BUY} icon={UserRound} title="Buyer" description="I represent the buyer" testId="new-deal-type-buy" />
            <SideOption value={DealType.SELL} selected={dealType === DealType.SELL} icon={KeyRound} title="Seller" description="I represent the seller" testId="new-deal-type-sell" />
          </ToggleGroup>
        </div>

        <SectionHeading className="mt-7">Client details</SectionHeading>
        <div className="mt-3 flex flex-col gap-3">
          <FormInput
            name="client_name"
            label={dealType === DealType.BUY ? "Buyer name" : "Seller name"}
            placeholder="Full name"
            autoComplete="name"
            className={NEW_DEAL_INPUT_CLASS}
            data-testid="new-deal-client-name"
            required
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormInput
              name="client_email"
              label="Email address"
              type="email"
              placeholder="name@example.com"
              autoComplete="email"
              className={NEW_DEAL_INPUT_CLASS}
              data-testid="new-deal-client-email"
              required
            />
            <PhoneField
              name="client_phone"
              countryName="client_phone_country"
              label="Phone number (optional)"
              testId="new-deal-client-phone"
              showResolved={false}
            />
          </div>
          <p className="-mt-1 text-[11px] text-dash-faint">An invitation will be emailed when you create the deal.</p>

          {additionalClients.fields.map((field, index) => (
            <div key={field.id} className="rounded-xl border border-dash-border p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-dash-ink">
                    Co-{dealType === DealType.BUY ? "buyer" : "seller"} {index + 2}
                  </p>
                  <p className="mt-0.5 text-[11px] text-dash-faint">They'll get an email invite to the deal room once you create it.</p>
                </div>
                <Button type="button" variant="ghost" size="icon" className="size-6" onClick={() => additionalClients.remove(index)} data-testid={`new-deal-remove-client-${index}`} aria-label="Remove">
                  <X className="size-4" />
                </Button>
              </div>
              <div className="mt-2 flex flex-col gap-3">
                <FormInput
                  name={`additional_clients.${index}.name`}
                  label="Name"
                  placeholder="e.g. Jordan Ellis"
                  autoComplete="name"
                  className={NEW_DEAL_INPUT_CLASS}
                  data-testid={`new-deal-client-name-${index}`}
                  required
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormInput
                    name={`additional_clients.${index}.email`}
                    label="Email"
                    type="email"
                    placeholder="e.g. jordan@example.com"
                    autoComplete="email"
                    className={NEW_DEAL_INPUT_CLASS}
                    data-testid={`new-deal-client-email-${index}`}
                    required
                  />
                  <PhoneField
                    name={`additional_clients.${index}.phone`}
                    countryName={`additional_clients.${index}.phone_country`}
                    label="Phone"
                    testId={`new-deal-client-phone-${index}`}
                    showResolved={false}
                  />
                </div>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="self-start px-0 font-semibold text-dash-greenDeep hover:bg-transparent hover:text-dash-greenDeep hover:underline"
            onClick={() => additionalClients.append({ name: "", email: "", phone: "", phone_country: DEFAULT_COUNTRY })}
            data-testid="new-deal-add-client"
          >
            <Plus className="size-4" /> Add {dealType === DealType.BUY ? "co-buyer" : "co-seller"}
          </Button>

          <div className="rounded-xl border border-dash-greenDeep/20 bg-dash-greenDeep/5 p-4">
            <FormCheckbox
              name="under_contract"
              label="Already under contract?"
              description="Start with the under-contract workflow. The property address is required."
              data-testid="new-deal-under-contract"
            />
          </div>

          {/* The other side only exists once there is a contract, so their agent
              is asked for alongside that flag rather than on every new deal. */}
          {underContract && (
            <div className="rounded-xl border border-dash-border p-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-dash-ink">Other side&apos;s agent</p>
                <p className="mt-0.5 text-[11px] text-dash-faint">Optional. They&apos;ll get limited view-only access to this deal.</p>
              </div>
              <div className="mt-2 flex flex-col gap-3">
                <FormInput
                  name="co_agent_name"
                  label={dealType === DealType.BUY ? "Listing agent name" : "Buyer agent name"}
                  placeholder="e.g. Morgan Lee"
                  autoComplete="name"
                  className={NEW_DEAL_INPUT_CLASS}
                  data-testid="new-deal-co-agent-name"
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <FormInput
                    name="co_agent_email"
                    label={dealType === DealType.BUY ? "Listing agent email" : "Buyer agent email"}
                    type="email"
                    placeholder="e.g. morgan@example.com"
                    autoComplete="email"
                    className={NEW_DEAL_INPUT_CLASS}
                    data-testid="new-deal-co-agent-email"
                  />
                  <PhoneField
                    name="co_agent_phone"
                    countryName="co_agent_phone_country"
                    label="Phone"
                    testId="new-deal-co-agent-phone"
                    showResolved={false}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
}

function PropertyStep({ form, setForm, onAdvance, showStepCount }) {
  const addressRequired = isAddressRequired(form);
  const propertyForm = useForm({
    resolver: zodResolver(newDealPropertySchema(addressRequired)),
    defaultValues: {
      address: form.address,
      country: form.country,
      city: form.city,
      state: form.state,
      zip_code: form.zip_code,
      list_price: form.list_price,
      close_date: form.close_date || "",
      financing: form.financing,
    },
  });

  const advance = (values) => {
    // Unchecking "under contract" hides the other side's agent; drop whatever
    // was typed there so it is neither invited nor shown on the review step.
    const next = values.under_contract
      ? values
      : { ...values, co_agent_name: "", co_agent_email: "", co_agent_phone: "" };
    setForm((current) => ({ ...current, ...next }));
    onAdvance();
  };

  return (
    <Form {...propertyForm}>
      <form id="new-deal-step-form" onSubmit={propertyForm.handleSubmit(advance)} data-testid="new-deal-step-2" noValidate>
        <StepHeader
          step={2}
          title="Property"
          description={addressRequired ? "Address & price" : "Optional until you have a property"}
          showStepCount={showStepCount}
        />
        {!addressRequired && (
          <p className="mt-4 rounded-xl border border-dash-border bg-dash-page px-4 py-3 text-xs leading-5 text-dash-muted" data-testid="new-deal-address-optional-note">
            Still house hunting? Leave this blank and open the deal now — you can add the
            address from the deal page as soon as you have one.
          </p>
        )}
        <div className="mt-5 flex flex-col gap-3">
          <FormInput name="address" label="Property address" placeholder="e.g. 412 Linden Avenue" autoComplete="street-address" className={NEW_DEAL_INPUT_CLASS} data-testid="new-deal-address" required={addressRequired} />
          <FormCountry name="country" label="Country" triggerClassName={NEW_DEAL_INPUT_CLASS} data-testid="new-deal-country" required={addressRequired} />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormState name="state" countryName="country" label="State" triggerClassName={NEW_DEAL_INPUT_CLASS} data-testid="new-deal-state" required={addressRequired} />
            <FormCity name="city" stateName="state" countryName="country" label="City" triggerClassName={NEW_DEAL_INPUT_CLASS} data-testid="new-deal-city" required={addressRequired} />
          </div>
          <div className="sm:max-w-[calc(50%_-_0.5rem)]">
            <FormInput name="zip_code" label="Postal code" autoComplete="postal-code" placeholder="11217" className={NEW_DEAL_INPUT_CLASS} data-testid="new-deal-zip" required={addressRequired} />
          </div>
          <FormInput name="list_price" label={form.under_contract ? "Purchase price" : "List / purchase price"} type="number" inputMode="decimal" min="0" step="0.01" placeholder="750000" className={NEW_DEAL_INPUT_CLASS} data-testid="new-deal-price" />
          {/* An under-contract deal already has these terms; they flow straight
              into Contract Details so nothing is typed twice. */}
          {form.under_contract && (
            <div className="grid gap-3 sm:grid-cols-2">
              <FormInput name="close_date" label="Closing date" type="date" className={NEW_DEAL_INPUT_CLASS} data-testid="new-deal-close-date" />
              <FormSelect
                name="financing"
                label="Purchase method"
                options={[{ value: "financed", label: "Financed" }, { value: "cash", label: "Cash" }]}
                triggerClassName={NEW_DEAL_INPUT_CLASS}
                data-testid="new-deal-financing"
              />
            </div>
          )}
          {/* Optional. Uploads on pick and lives on the wizard's own state, not
              this step's form, so going back and forth keeps it. */}
          <div>
            <p className="mb-1.5 text-sm font-medium text-dash-ink">Cover photo <span className="font-normal text-dash-faint">(optional)</span></p>
            <ImageDrop
              value={form.hero_image_url || ""}
              onChange={(url) => setForm((current) => ({ ...current, hero_image_url: url }))}
              label="Add a listing photo"
              helperText="You can add or change it later from the deal page"
              className="aspect-auto h-36 overflow-hidden rounded-xl"
              testId="new-deal-cover"
            />
          </div>
        </div>
      </form>
    </Form>
  );
}

function ReviewStep({ form, fullAddress, onSubmit, onEditStep, showStepCount }) {
  const reviewForm = useForm({ resolver: zodResolver(newDealReviewSchema), defaultValues: {} });
  const isBuy = form.deal_type === DealType.BUY;
  const party = isBuy ? "buyer" : "seller";
  const Party = isBuy ? "Buyer" : "Seller";
  const coClients = (form.additional_clients || []).filter((c) => c.name);
  const hasCoAgent = form.co_agent_name || form.co_agent_email || form.co_agent_phone;
  const stage = form.under_contract ? "Under contract" : isBuy ? "Pre-approval" : "Pre-listing";
  const cityLine = [form.city, [form.state, form.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  // The footer disables Create on the same check; this guard covers Enter.
  const submit = () => { if (!clientNamesNeedReview(form)) onSubmit(); };
  return (
    <Form {...reviewForm}>
    <form id="new-deal-step-form" onSubmit={reviewForm.handleSubmit(submit)} data-testid="new-deal-step-3" noValidate>
      <StepHeader step={3} title="Ready to create your deal?" description="Review the details below. You can edit anything before continuing." showStepCount={showStepCount} />

      <section className="mt-6 flex items-start gap-4 rounded-2xl bg-dash-searchBg p-4 sm:p-5" data-testid="new-deal-review-summary">
        {form.hero_image_url ? (
          <img src={absoluteUrl(form.hero_image_url)} alt="" className="size-16 shrink-0 rounded-xl object-cover" data-testid="new-deal-review-cover" />
        ) : (
          <Home className="mt-1 size-7 shrink-0 text-dash-ink" strokeWidth={1.5} aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          {/* Says what will actually appear on the board, rather than an em dash
              that reads as a field the agent forgot to fill in. */}
          <p className="break-words font-display text-xl text-dash-ink">{form.address || (fullAddress ? fullAddress : isBuy ? "No property yet — house hunting" : "—")}</p>
          {form.address && cityLine && <p className="mt-0.5 text-sm text-dash-faint">{cityLine}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge>{Party} representation</Badge>
            <Badge>{stage}</Badge>
          </div>
        </div>
        {form.list_price && (
          <div className="shrink-0 text-right">
            <p className="font-display text-xl text-dash-ink">{formatDealPrice(form.list_price)}</p>
            <p className="mt-0.5 text-xs text-dash-faint">{form.under_contract ? "Purchase price" : "List price"}</p>
          </div>
        )}
      </section>

      {/* Every person entered in step 1, with every detail, so the agent can
          catch a typo before invites go out. */}
      <ReviewSection title="Client details" onEdit={() => onEditStep(1)} testId="new-deal-review-edit-client">
        <PersonRows heading={coClients.length ? Party : undefined} label={`${Party} name`} person={{ name: form.client_name, email: form.client_email, phone: form.client_phone }} address={form.address} />
        {coClients.map((c, i) => (
          <PersonRows key={i} heading={`Co-${party} ${i + 2}`} label="Name" person={c} address={form.address} testId={`new-deal-review-co-client-${i}`} />
        ))}
      </ReviewSection>

      {hasCoAgent && (
        <ReviewSection title="Other side's agent" onEdit={() => onEditStep(1)}>
          <PersonRows
            label={isBuy ? "Listing agent" : "Buyer agent"}
            person={{ name: form.co_agent_name, email: form.co_agent_email, phone: form.co_agent_phone }}
          />
        </ReviewSection>
      )}

      <ReviewSection title="Property details" onEdit={() => onEditStep(2)} testId="new-deal-review-edit-property">
        <ReviewRow label="Address" value={fullAddress || (isBuy ? "No property yet — house hunting" : "—")} />
        <ReviewRow label="Property type" value="Residential" />
        <ReviewRow label="Representation" value={Party} />
        <ReviewRow label="Starting stage" value={stage} />
        <ReviewRow label="Price" value={formatDealPrice(form.list_price)} />
        {form.under_contract && (
          <>
            <ReviewRow label="Closing" value={form.close_date || "—"} />
            <ReviewRow label="Purchase" value={form.financing === "cash" ? "Cash" : "Financed"} />
          </>
        )}
      </ReviewSection>

      <p className="mt-6 flex items-center gap-2 text-xs text-dash-faint">
        <Mail className="size-4 shrink-0" aria-hidden="true" /> The client invitation will be sent when you create the deal.
      </p>
    </form>
    </Form>
  );
}

function PersonRows({ heading = undefined, label, person, address = undefined, testId = undefined }) {
  const flagged = address !== undefined && looksLikeAddress(person.name, address);
  return (
    <div className="flex flex-col gap-3 border-b border-dash-border pb-4 last:border-0 last:pb-0" data-testid={testId}>
      {heading && <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-dash-muted">{heading}</p>}
      <ReviewRow
        label={label}
        value={flagged ? (
          <span className="flex items-start gap-2 text-left" data-testid="new-deal-review-name-flag">
            <CircleAlert className="mt-0.5 size-4 shrink-0 text-rust" aria-hidden="true" />
            <span>
              <span className="block font-semibold text-rust">Review required</span>
              <span className="block font-normal text-dash-faint">A property address was entered as the {label.toLowerCase()}.</span>
            </span>
          </span>
        ) : person.name || "—"}
      />
      <ReviewRow label="Email address" value={person.email || "—"} />
      <ReviewRow label="Phone number" value={person.phone || "—"} />
    </div>
  );
}

function StepHeader({ step, title, description, showStepCount = true }) {
  return (
    <header>
      {showStepCount && <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-dash-greenDeep">Step {step} of 3</p>}
      <h2 className="mt-1 font-display text-3xl tracking-tight text-dash-ink">{title}</h2>
      <p className="mt-1 text-sm text-dash-faint">{description}</p>
    </header>
  );
}

function SectionHeading({ children, className = "" }) {
  return <h3 className={cn("font-display text-lg text-dash-ink", className)}>{children}</h3>;
}

function Badge({ children }) {
  return <span className="rounded-md bg-dash-greenDeep/10 px-2.5 py-1 text-xs font-medium text-dash-ink">{children}</span>;
}

function SideOption({ value, selected, icon: Icon, title, description, testId }) {
  return (
    <ToggleGroupItem value={value} className={cn("h-auto w-full justify-start gap-4 rounded-2xl border bg-white p-4 text-left shadow-none hover:bg-dash-page data-[state=on]:border-dash-greenDeep data-[state=on]:bg-dash-greenDeep/5 data-[state=on]:text-dash-ink", selected ? "border-dash-greenDeep" : "border-dash-border")} data-testid={testId}>
      <Icon className="size-6 shrink-0 text-dash-ink" strokeWidth={1.5} aria-hidden="true" />
      <span className="flex-1"><strong className="block text-sm font-semibold">{title}</strong><span className="mt-0.5 block text-xs font-normal text-dash-faint">{description}</span></span>
      <span className={cn("size-4 shrink-0 rounded-full border-2", selected ? "border-dash-greenDeep bg-dash-greenDeep shadow-[inset_0_0_0_3px_white]" : "border-dash-border")} />
    </ToggleGroupItem>
  );
}

function ReviewSection({ title, onEdit, testId = undefined, children }) {
  return (
    <section className="mt-6 border-t border-dash-border pt-5">
      <header className="flex items-center justify-between gap-3">
        <SectionHeading>{title}</SectionHeading>
        <Button type="button" variant="ghost" size="sm" onClick={onEdit} className="text-dash-ink" data-testid={testId}>
          <Pencil className="size-3.5" /> Edit
        </Button>
      </header>
      <dl className="mt-3 flex flex-col gap-3">{children}</dl>
    </section>
  );
}

function ReviewRow({ label, value }) {
  return <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 text-sm"><dt className="text-dash-faint">{label}</dt><dd className="min-w-0 break-words font-medium text-dash-ink">{value}</dd></div>;
}
